/**
 * Hook for observatory loading, selection, and telescope configuration.
 */
import { useState, useCallback } from 'react';
import { apiService } from '../../../services/apiService';
import { loadSavedPreferences } from './usePreferences';

const DEFAULT_TELESCOPE_CONFIG = {
  telescopeAperture: 200,
  focalLength: 2000,
  cameraPixelSize: 4.5,
  readoutTime: 10,
  minExposure: 30,
  maxExposure: 300,
  defaultExposure: 120,
  filters: ['Clear', 'V', 'R'],
  selectedFilter: 'Clear',
  binning: 1,
};

/**
 * Apply observatory telescope config onto the state.
 */
function applyObservatoryConfig(observatory, setTelescopeConfig) {
  const filters = observatory.capabilities?.available_filters || observatory.available_filters;
  const telescopeConfigFromObs = observatory.configuration?.telescope_config || {};

  setTelescopeConfig(prev => ({
    ...prev,
    telescopeAperture: telescopeConfigFromObs.aperture_mm || prev.telescopeAperture,
    focalLength: telescopeConfigFromObs.focal_length_mm || prev.focalLength,
    cameraPixelSize: telescopeConfigFromObs.camera_pixel_size_um || prev.cameraPixelSize,
    readoutTime: telescopeConfigFromObs.readout_time_seconds || prev.readoutTime,
    minExposure: telescopeConfigFromObs.min_exposure_seconds || prev.minExposure,
    maxExposure: telescopeConfigFromObs.max_exposure_seconds || prev.maxExposure,
    defaultExposure: telescopeConfigFromObs.default_exposure_seconds || prev.defaultExposure,
    binning: telescopeConfigFromObs.default_binning || prev.binning,
    filters: filters && filters.length > 0 ? filters : prev.filters,
    selectedFilter: filters && filters.length > 0 ? filters[0] : prev.selectedFilter,
  }));
  console.log('Loaded telescope config from observatory:', telescopeConfigFromObs);

  return filters;
}

export const useObservatoryConfig = ({
  user,
  updateUserPreferences,
  setFilters,
  setBaselineBefore,
  setBaselineAfter,
  setMinAltitude,
  setShowOnlyFullTransits,
}) => {
  const [observatories, setObservatories] = useState([]);
  const [selectedObservatoryId, setSelectedObservatoryId] = useState(null);
  const [observatoryLocation, setObservatoryLocation] = useState(null);
  const [telescopeConfig, setTelescopeConfig] = useState(DEFAULT_TELESCOPE_CONFIG);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null);

  const setLocationFromObservatory = useCallback((observatory) => {
    if (observatory?.location) {
      setObservatoryLocation({
        latitude: observatory.location.latitude,
        longitude: observatory.location.longitude,
        altitude: observatory.location.altitude_meters || 0,
        timezone: observatory.location.timezone || 'UTC',
      });
    }
  }, []);

  const loadObservatoryLocation = useCallback(async () => {
    try {
      const response = await apiService.getObservatories();
      if (!response.data?.observatories?.length) return;

      const observatoryList = response.data.observatories;
      setObservatories(observatoryList);

      const savedObsId = user?.preferences?.last_observatory_id;
      const observatory = (savedObsId && observatoryList.find(o => o._id === savedObsId))
        || observatoryList[0];
      setSelectedObservatoryId(observatory._id);
      setLocationFromObservatory(observatory);

      const filters = applyObservatoryConfig(observatory, setTelescopeConfig);
      const availableFilters = filters && filters.length > 0 ? filters : [];

      // Load saved preferences on top of observatory defaults
      await loadSavedPreferences({
        availableFilters,
        setFilters,
        setTelescopeConfig,
        setBaselineBefore,
        setBaselineAfter,
        setMinAltitude,
        setShowOnlyFullTransits,
      });
    } catch (error) {
      console.error('Failed to load observatory location:', error);
    }
  }, [user, setFilters, setBaselineBefore, setBaselineAfter, setMinAltitude, setShowOnlyFullTransits, setLocationFromObservatory]);

  const handleObservatoryChange = useCallback((observatoryId) => {
    setSelectedObservatoryId(observatoryId);
    if (observatoryId) {
      updateUserPreferences({ last_observatory_id: observatoryId });
    }
    const observatory = observatories.find(o => o._id === observatoryId);
    if (observatory?.location) {
      setLocationFromObservatory(observatory);
      const filters = observatory.capabilities?.available_filters || observatory.available_filters;
      const telescopeConfigFromObs = observatory.configuration?.telescope_config || {};

      setTelescopeConfig(prev => ({
        ...prev,
        telescopeAperture: telescopeConfigFromObs.aperture_mm || prev.telescopeAperture,
        focalLength: telescopeConfigFromObs.focal_length_mm || prev.focalLength,
        cameraPixelSize: telescopeConfigFromObs.camera_pixel_size_um || prev.cameraPixelSize,
        readoutTime: telescopeConfigFromObs.readout_time_seconds || prev.readoutTime,
        minExposure: telescopeConfigFromObs.min_exposure_seconds || prev.minExposure,
        maxExposure: telescopeConfigFromObs.max_exposure_seconds || prev.maxExposure,
        defaultExposure: telescopeConfigFromObs.default_exposure_seconds || prev.defaultExposure,
        binning: telescopeConfigFromObs.default_binning || prev.binning,
        filters: filters && filters.length > 0 ? filters : prev.filters,
        selectedFilter: filters && filters.length > 0 ?
          (filters.includes(prev.selectedFilter) ? prev.selectedFilter : filters[0]) : prev.selectedFilter
      }));
      console.log('Loaded telescope config from observatory:', telescopeConfigFromObs);
    }
  }, [observatories, updateUserPreferences, setLocationFromObservatory]);

  const saveTelescopeConfig = useCallback(async ({ onSavePreferences, setError }) => {
    if (!selectedObservatoryId) {
      setError({ type: 'error', message: 'Please select an observatory first' });
      return;
    }

    try {
      const updateData = {
        configuration: {
          telescope_config: {
            aperture_mm: parseFloat(telescopeConfig.telescopeAperture),
            focal_length_mm: parseFloat(telescopeConfig.focalLength),
            camera_pixel_size_um: parseFloat(telescopeConfig.cameraPixelSize),
            readout_time_seconds: parseFloat(telescopeConfig.readoutTime),
            min_exposure_seconds: parseFloat(telescopeConfig.minExposure),
            max_exposure_seconds: parseFloat(telescopeConfig.maxExposure),
            default_exposure_seconds: parseFloat(telescopeConfig.defaultExposure),
            default_binning: parseInt(telescopeConfig.binning)
          }
        }
      };

      console.log('Saving telescope config to observatory:', selectedObservatoryId, updateData);
      await apiService.updateObservatory(selectedObservatoryId, updateData);

      setError({ type: 'success', message: 'Telescope configuration saved successfully' });

      if (onSavePreferences) onSavePreferences();

      // Reload observatory to confirm save
      const response = await apiService.getObservatories();
      const updated = response.data?.observatories?.find(o => o._id === selectedObservatoryId);
      if (updated) {
        setObservatories(prev => prev.map(o => o._id === selectedObservatoryId ? updated : o));
      }
      return true; // success
    } catch (error) {
      console.error('Failed to save telescope configuration:', error);
      setError({
        type: 'error',
        message: 'Failed to save telescope configuration: ' + (error.response?.data?.message || error.message)
      });
      return false;
    }
  }, [selectedObservatoryId, telescopeConfig]);

  const loadProjects = useCallback(async () => {
    try {
      // GET /v1/projects is paginated (default 20, capped 100 per page), so
      // getAllProjects walks every page — otherwise a user's own project can be
      // silently dropped past the first page. Match the general observation flow
      // (ProjectStep), which lists active projects.
      const projectList = await apiService.getAllProjects({ status: 'active' });
      if (Array.isArray(projectList)) {
        setProjects(projectList);
        if (projectList.length > 0) {
          setSelectedProjectId(projectList[0]._id);
        }
      }
    } catch (err) {
      console.error('Failed to load projects:', err);
      try {
        const newProject = await apiService.createProject({
          name: 'Exoplanet Transits',
          description: 'Automated exoplanet transit observations',
          pi_name: user?.name || 'Unknown',
          institution: user?.institution || 'Unknown',
        });
        if (newProject.data) {
          setProjects([newProject.data]);
          setSelectedProjectId(newProject.data._id);
        }
      } catch (createErr) {
        console.error('Failed to create default project:', createErr);
      }
    }
  }, [user]);

  return {
    observatories,
    setObservatories,
    selectedObservatoryId,
    setSelectedObservatoryId,
    observatoryLocation,
    telescopeConfig,
    setTelescopeConfig,
    projects,
    selectedProjectId,
    setSelectedProjectId,
    loadObservatoryLocation,
    handleObservatoryChange,
    saveTelescopeConfig,
    loadProjects,
  };
};
